import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ZoomableImage.css';

const ZoomableImage = ({ 
  src, 
  alt, 
  style = {}, 
  className = '',
  loading = 'eager',
  isMobile = false 
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPinchDistance, setLastPinchDistance] = useState(0);
  const modalRef = useRef(null);
  const imageRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Reset zoom state when modal closes
  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  // Open zoom modal
  const openZoom = () => {
    setIsZoomed(true);
    resetZoom();
    document.body.style.overflow = 'hidden';
  };

  // Close zoom modal
  const closeZoom = useCallback(() => {
    setIsZoomed(false);
    resetZoom();
    document.body.style.overflow = '';
  }, [resetZoom]);

  // Button click handlers with proper event handling
  const handleZoomInClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(prevScale => Math.min(prevScale * 1.2, 5));
  }, []);

  const handleZoomOutClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(prevScale => Math.max(prevScale / 1.2, 0.5));
  }, []);

  const handleResetClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleCloseClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    closeZoom();
  }, [closeZoom]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isZoomed) return;
      
      if (e.key === 'Escape') {
        closeZoom();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomInClick({ preventDefault: () => {}, stopPropagation: () => {} });
      } else if (e.key === '-') {
        e.preventDefault();
        handleZoomOutClick({ preventDefault: () => {}, stopPropagation: () => {} });
      }
    };

    if (isZoomed) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isZoomed, closeZoom, handleZoomInClick, handleZoomOutClick]);

  // Handle wheel zoom
  const handleWheel = useCallback((e) => {
    if (!isZoomed) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prevScale => {
      const newScale = Math.max(0.5, Math.min(5, prevScale * delta));
      return newScale;
    });
  }, [isZoomed]);

  // Handle mouse/touch events for dragging
  const handleMouseDown = useCallback((e) => {
    if (!isZoomed || scale <= 1) return;
    
    // Don't start dragging if clicking on any button or control element
    const isButton = e.target.closest('button') || 
                    e.target.closest('.zoom-control-btn') || 
                    e.target.closest('.zoom-close-btn') || 
                    e.target.closest('.zoom-controls');
    
    if (isButton) {
      return; // Remove console.log
    }
    
    // Only start dragging on image itself
    if (!e.target.closest('.zoom-image')) {
      return;
    }
    
    setIsDragging(true); // Remove console.log
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    dragStartRef.current = {
      x: clientX - position.x,
      y: clientY - position.y
    };
  }, [isZoomed, scale, position]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !isZoomed || scale <= 1) return;
    
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setPosition({
      x: clientX - dragStartRef.current.x,
      y: clientY - dragStartRef.current.y
    });
  }, [isDragging, isZoomed, scale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle touch pinch zoom
  const handleTouchStart = useCallback((e) => {
    if (!isZoomed) return;
    
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setLastPinchDistance(distance);
    } else if (e.touches.length === 1) {
      handleMouseDown(e);
    }
  }, [isZoomed, handleMouseDown]);

  const handleTouchMove = useCallback((e) => {
    if (!isZoomed) return;
    
    e.preventDefault();
    
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      
      if (lastPinchDistance > 0) {
        const deltaScale = distance / lastPinchDistance;
        setScale(prevScale => Math.max(0.5, Math.min(5, prevScale * deltaScale)));
      }
      
      setLastPinchDistance(distance);
    } else if (e.touches.length === 1) {
      handleMouseMove(e);
    }
  }, [isZoomed, lastPinchDistance, handleMouseMove]);

  // Add event listeners to modal - only for wheel zoom and touch events
  useEffect(() => {
    if (!isZoomed) return;
    
    const modal = modalRef.current;
    if (!modal) return;
    
    // Only add wheel event listener for scroll zoom
    modal.addEventListener('wheel', handleWheel, { passive: false });
    
    // Add touch events for mobile
    modal.addEventListener('touchstart', handleTouchStart, { passive: false });
    modal.addEventListener('touchmove', handleTouchMove, { passive: false });
    modal.addEventListener('touchend', handleMouseUp);
    
    return () => {
      modal.removeEventListener('wheel', handleWheel);
      modal.removeEventListener('touchstart', handleTouchStart);
      modal.removeEventListener('touchmove', handleTouchMove);
      modal.removeEventListener('touchend', handleMouseUp);
    };
  }, [isZoomed, handleWheel, handleTouchStart, handleTouchMove, handleMouseUp]);

  // Add mouse event listeners only to the image container for desktop dragging
  useEffect(() => {
    if (!isZoomed) return;
    
    const handleDocumentMouseMove = (e) => {
      if (!isDragging || !isZoomed || scale <= 1) return;
      
      e.preventDefault();
      const clientX = e.clientX;
      const clientY = e.clientY;
      
      setPosition({
        x: clientX - dragStartRef.current.x,
        y: clientY - dragStartRef.current.y
      });
    };

    const handleDocumentMouseUp = () => {
      setIsDragging(false);
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
      };
    }
  }, [isDragging, isZoomed, scale]);

  return (
    <>
      {/* Main image with zoom button */}
      <div className={`zoomable-image-container ${className}`} style={style}>
        <img
          src={src}
          alt={alt}
          loading={loading}
          className="zoomable-image"
          style={style}
        />
        <button
          className="zoom-button"
          onClick={openZoom}
          aria-label="Zoom image"
          title="Click to zoom"
        >
          <span role="img" aria-label="magnifying glass">🔍</span>
        </button>
      </div>

      {/* Zoom modal */}
      {isZoomed && (
        <div className="zoom-modal" ref={modalRef} onClick={closeZoom}>
          <div 
            className="zoom-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Controls */}
            <div className="zoom-controls">
              <button
                className="zoom-control-btn"
                onClick={handleZoomInClick}
                type="button"
                aria-label="Zoom in"
                title="Zoom in"
              >
                <span className="zoom-icon">+</span>
              </button>
              
              <button
                className="zoom-control-btn"
                onClick={handleZoomOutClick}
                type="button"
                aria-label="Zoom out"
                title="Zoom out"
              >
                <span className="zoom-icon">−</span>
              </button>
              
              <button
                className="zoom-control-btn"
                onClick={handleResetClick}
                type="button"
                aria-label="Reset zoom"
                title="Reset zoom"
              >
                <span className="zoom-icon">⌂</span>
              </button>
              
              <button
                className="zoom-close-btn"
                onClick={handleCloseClick}
                type="button"
                aria-label="Close zoom"
                title="Close (Esc)"
              >
                <span className="close-icon">×</span>
              </button>
            </div>

            {/* Zoomed image */}
            <div 
              className="zoom-image-container" 
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleMouseDown}
            >
              <img
                ref={imageRef}
                src={src}
                alt={alt}
                className="zoom-image zoom-image-force-transform"
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                  cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                draggable={false}
                onLoad={() => {}}
              />
            </div>

            {/* Instructions */}
            <div className="zoom-instructions">
              {isMobile ? (
                <p>Pinch to zoom • Drag to pan • Tap outside to close</p>
              ) : (
                <p>Mouse wheel to zoom • Drag to pan • ESC to close</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ZoomableImage;
